import { supabase } from '../lib/supabase';
import { imageExtension, mediaPath } from '../lib/creatorMedia';
import { CREATORS } from '../data/creators';
import { DEMO_PRODUCTS, DEMO_CAMPAIGNS, DEMO_CHATS } from '../data/demo';
import { INITIAL_APPLICATION_STATUS, OPEN_CAMPAIGN_STATUS } from '../types';
import type {
  Application,
  ApplicationRow,
  ApplicationStatus,
  Campaign,
  CampaignRow,
  CampaignStatus,
  Chat,
  ChatMessage,
  ChatRow,
  Creator,
  CreatorReview,
  CreatorRow,
  MessageRole,
  NewApplicationFields,
  NewCreatorFields,
  Product,
} from '../types';

/**
 * A campaign as returned by the data layer: the app-facing shape plus the raw
 * brief columns (`brief_path`/`brief_name`) that the campaign view hydrates
 * into a short-lived signed download link. Demo rows omit the brief columns.
 */
type CampaignRecord = Campaign & Partial<Pick<CampaignRow, 'brief_path' | 'brief_name'>>;

// Private Storage bucket holding campaign brief PDFs. Objects live at
// `<owner_uid>/<campaign_id>/<filename>`; access is governed by the RLS
// policies in supabase/pitch_and_briefs.sql. Mirrors the bucket id there.
const BRIEF_BUCKET = 'campaign-briefs';
// How long a download link stays valid. The bucket is private, so cards render
// short-lived signed URLs minted at load time rather than public URLs.
const BRIEF_URL_TTL = 60 * 60; // 1 hour

// Private Storage bucket holding creator avatars and portfolio images. Objects
// live at `<owner_uid>/avatar-<seed>.<ext>` and
// `<owner_uid>/portfolio/<seed>.<ext>`; access is governed by the policies in
// supabase/migrations/20260811153203_creator_media.sql. Mirrors the bucket id there.
const CREATOR_MEDIA_BUCKET = 'creator-media';
const MEDIA_URL_TTL = 60 * 60; // 1 hour

// Unique-enough suffix for a media key. Replacing an avatar writes a new object
// rather than overwriting a fixed name, so a stale CDN or browser cache can
// never keep showing the old picture.
function mediaSeed(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

// ── HELPERS ──────────────────────────────────────────────────────────────────

/**
 * Does this conversation hold anything the viewer has not seen? (PIT-86)
 *
 * Computed by comparing the viewer's last-read marker against the messages
 * themselves, rather than read from a flag someone has to remember to reset —
 * which is exactly how `chats.unread` ended up permanently wrong.
 *
 * Your own messages never count. A message with no sender predates PIT-80 and
 * is treated as the other side's, which is the cautious reading: showing a dot
 * that opening the thread clears beats hiding something unseen.
 */
export function unreadFor(
  row: ChatRow,
  viewerId: string | null | undefined,
  lastReadMessageId: number
): boolean {
  if (!viewerId) return false;
  return (row.messages ?? []).some(
    (m) => m.id > lastReadMessageId && m.sender_user_id !== viewerId
  );
}

/** The newest message in a conversation, or 0 when it has none. */
export function latestMessageId(chat: Pick<Chat, 'messages'>): number {
  return chat.messages.reduce((max, m) => (m.id && m.id > max ? m.id : max), 0);
}

export function mapChat(row: ChatRow, viewerId?: string | null, lastReadMessageId = 0): Chat {
  // Both participants read the same row, so the thread's label depends on who
  // is looking: you want to see the person you are talking to, not yourself.
  // `name`/`init` are the creator side (their meaning since the single-sided
  // era), `brand_name`/`brand_init` the brand side, so the creator gets the
  // brand's label and everyone else gets the creator's.
  //
  // Falls back to `name`/`init` when the brand label is missing — threads that
  // predate PIT-81 have none, and a thread with a name is better than a blank.
  const viewingAsCreator = Boolean(viewerId) && row.creator_user_id === viewerId;
  const counterpartName = viewingAsCreator ? (row.brand_name ?? row.name) : row.name;
  const counterpartInit = viewingAsCreator ? (row.brand_init ?? row.init) : row.init;

  // Raw rows carry snake_case columns and nullable text; the cast marks the
  // boundary where a DB row becomes the app-facing `Chat` shape.
  return {
    id: row.id,
    name: counterpartName,
    init: counterpartInit,
    campaign: row.campaign,
    status: row.status,
    statusClass: row.status_class,
    unread: unreadFor(row, viewerId, lastReadMessageId),
    revisions: row.revisions,
    maxRevisions: row.max_revisions,
    preview: row.preview,
    messages: (row.messages || [])
      .sort((a, b) => a.id - b.id)
      .map((m) => ({
        id: m.id,
        from: m.from_role as MessageRole,
        text: m.message_text,
        file: m.file_name,
        fileType: m.file_type,
        delivery: m.delivery,
        time: m.sent_time,
      })),
  } as Chat;
}

export function mapCreator(row: CreatorRow): Creator {
  // The app treats a creator row as a `Creator`; the cast marks the row→app
  // boundary (snake_case columns are read directly by the profile view).
  return {
    ...row,
    reviews: (row.creator_reviews || []).map((r) => ({
      id: r.id,
      brand: r.brand,
      rating: r.rating,
      text: r.review_text,
      date: r.review_date,
    })),
  } as unknown as Creator;
}

// Bundled fallback when Supabase isn't configured (demo mode): the app stays
// a browsable showcase, writes silently no-op.
const demoCreators = CREATORS.map((c) => {
  // Drop `saved` (a per-user UI flag, not a DB column) and rename `rateNum` to
  // the snake_case `rate_num` the creators table uses. The ownership columns
  // mirror what the PIT-45 migration backfills onto the seeded rows: no owner,
  // fabricated, and visible to everyone.
  // Bundled creators have no uploaded media — their emoji and gradient are the
  // whole visual, which is exactly the fallback real profiles use before they
  // upload anything (PIT-71).
  const { rateNum, saved: _saved, ...rest } = c;
  return {
    ...rest,
    rate_num: rateNum,
    user_id: null,
    published: true,
    is_demo: true,
    avatar_path: null,
    portfolio_paths: [],
  };
});

// The profile the dashboard falls back to in demo mode. Seeded rows are
// 1-indexed and this is the first of them (Jordan Reeves), so the showcase
// dashboard keeps rendering with no Supabase configured.
const DEMO_DASHBOARD_CREATOR = demoCreators[0];

/**
 * Raised when the database rejects a handle — either taken (23505 on
 * `creators_handle_lower_key`) or reserved (23514 on
 * `creators_handle_not_reserved`). Callers surface `message` on the handle
 * field; the raw PostgREST error never reaches the creator.
 */
export class HandleUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'HandleUnavailableError';
  }
}

/**
 * Raised when a creator applies to a campaign they have already applied to.
 * The `applications_campaign_creator_key` index is what decides — two tabs will
 * race it, and the loser must be told plainly rather than shown a raw error.
 */
export class ApplicationExistsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ApplicationExistsError';
  }
}

/**
 * Is this the duplicate-application violation, as opposed to some other 23505?
 *
 * Matched on the constraint name for the same reason the handle mapping is:
 * `createMyCreator` treats a 23505 on `creators.user_id` as "this row is
 * already yours", and a different unique violation reaching that branch would
 * hand back a row nobody asked for. Never key on the code alone.
 */
function isDuplicateApplication(error: PostgrestErrorLike | null): boolean {
  if (!error || error.code !== '23505') return false;
  const text = `${error.message ?? ''} ${error.details ?? ''}`;
  return /applications_campaign_creator_key|campaign_id.*creator_id/i.test(text);
}

/** Shape of the PostgREST error fields the handle-constraint mapping reads. */
interface PostgrestErrorLike {
  code?: string;
  message?: string;
  details?: string;
}

/**
 * Translate a write error into a `HandleUnavailableError`, or null when it is
 * about something else.
 *
 * The constraint names come from the PIT-55 migration, and matching on them is
 * what keeps a taken *handle* from being mistaken for the `user_id` unique
 * violation that `createMyCreator` treats as "this row is already yours" — the
 * two are both 23505, and confusing them would silently hand back a row the
 * creator never made.
 */
function handleConstraintError(error: PostgrestErrorLike | null): HandleUnavailableError | null {
  if (!error) return null;
  const text = `${error.message ?? ''} ${error.details ?? ''}`;
  if (error.code === '23505' && /creators_handle_lower_key|lower\(handle\)/i.test(text)) {
    return new HandleUnavailableError('That handle is taken — please choose another.');
  }
  if (error.code === '23514' && /creators_handle_not_reserved/i.test(text)) {
    return new HandleUnavailableError('That handle is reserved — please choose another.');
  }
  if (error.code === '23514' && /creators_handle_format/i.test(text)) {
    return new HandleUnavailableError(
      'Handles are 3–30 characters, using letters, numbers, dots or underscores.'
    );
  }
  return null;
}

// Resolve the signed-in user's id from the local session. RLS is the real
// boundary — this only scopes the query so PostgREST returns the caller's own
// row instead of every published profile.
async function currentUserId(): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data?.session?.user?.id ?? null;
}

// Fetch the creator row owned by `userId`, or null when they have none yet.
// `maybeSingle` is deliberate: zero rows is an expected state (signed in, but
// onboarding not finished) rather than an error.
async function ownCreatorRow(userId: string): Promise<CreatorRow | null> {
  if (!supabase) return null;
  const { data } = await supabase.from('creators').select('*').eq('user_id', userId).maybeSingle();
  return (data as CreatorRow | null) ?? null;
}

// ── DATA ACCESS ──────────────────────────────────────────────────────────────

export const db = {
  demoMode: !supabase,

  // ── CREATORS ───────────────────────────────────────────────────────────────

  // The brand-facing Discover catalogue: published profiles only (PIT-63).
  //
  // Filtered in the query rather than the view so a half-built profile never
  // reaches the client to begin with. This narrows what RLS already allows
  // instead of substituting for it — the select policy keeps *other* people's
  // unpublished rows unreachable; this clause also drops the caller's own,
  // which RLS deliberately lets through so their dashboard can render it.
  async getCreators(): Promise<CreatorRow[]> {
    if (!supabase) return demoCreators;
    const { data } = await supabase.from('creators').select('*').eq('published', true).order('id');
    return data ?? [];
  },

  // A single profile by id, for the /creators/$id deep link.
  //
  // Deliberately not filtered on `published`: the select policy already decides
  // who may see an unpublished row (its owner, nobody else), so an outsider
  // following a link to a draft profile gets no row back and the view reads it
  // as not found — while the owner's own link keeps working. Adding a published
  // filter here would break that second case.
  async getCreatorById(id: number): Promise<Creator | null> {
    if (!supabase) {
      const c = demoCreators.find((x) => x.id === Number(id));
      return c ? ({ ...c } as unknown as Creator) : null;
    }
    const { data } = await supabase
      .from('creators')
      .select('*, creator_reviews(*)')
      .eq('id', id)
      .single();
    return data ? mapCreator(data) : null;
  },

  // Resolve the signed-in user's own creator row (`user_id = auth.uid()`).
  // Returns null when they are authenticated but haven't built a profile yet —
  // an expected state the dashboard renders an empty shell for. In demo mode
  // there is no session, so the bundled showcase profile stands in.
  async getMyCreator(): Promise<CreatorRow | null> {
    if (!supabase) return DEMO_DASHBOARD_CREATOR ?? null;
    const userId = await currentUserId();
    if (!userId) return null;
    return ownCreatorRow(userId);
  },

  // Create the signed-in user's creator row. Idempotent: if a row already
  // exists it is resumed rather than duplicated, both by checking first and by
  // treating the unique-violation a concurrent submit would raise as "already
  // yours". No-ops in demo mode; throws on a real write error so the caller can
  // surface a failure toast.
  async createMyCreator(fields: NewCreatorFields): Promise<CreatorRow | null> {
    if (!supabase) return null;
    const userId = await currentUserId();
    if (!userId) return null;

    const existing = await ownCreatorRow(userId);
    if (existing) return existing;

    const { data, error } = await supabase
      .from('creators')
      .insert({ ...fields, user_id: userId })
      .select()
      .single();
    if (error) {
      // A handle the database refused (taken or reserved) — distinct from the
      // user_id collision below, though both surface as 23505.
      const handleError = handleConstraintError(error);
      if (handleError) throw handleError;
      // 23505 = unique_violation on creators.user_id: another tab or a double
      // submit won the race, so the row this call wanted already exists.
      if (error.code === '23505') return ownCreatorRow(userId);
      throw error;
    }
    return (data as CreatorRow | null) ?? null;
  },

  // Is `handle` free? A courtesy check for the profile builder, not a guarantee:
  // RLS hides unpublished profiles from everyone but their owner, so a handle
  // held by an unpublished creator reads as free here. The unique index added in
  // PIT-55 is what actually decides, and the write path surfaces its rejection.
  // Returns true in demo mode (no backend to ask) and on a failed lookup, so a
  // hint that can't be fetched never blocks a creator from submitting.
  async isHandleAvailable(handle: string): Promise<boolean> {
    if (!supabase) return true;
    const normalized = handle.trim().toLowerCase();
    if (!normalized) return true;
    // `ilike` is a LIKE pattern, and handles may legitimately contain `_` —
    // which would otherwise match any single character and report a free handle
    // as taken. Escape the wildcards so the comparison stays exact.
    const pattern = normalized.replace(/[\\%_]/g, (c) => `\\${c}`);
    try {
      const { data, error } = await supabase
        .from('creators')
        .select('id, user_id')
        .ilike('handle', pattern)
        .limit(1);
      if (error) return true;
      const match = (data as Pick<CreatorRow, 'id' | 'user_id'>[] | null)?.[0];
      if (!match) return true;
      // Their own handle is still theirs — resuming onboarding must not report
      // the handle they already hold as taken.
      return match.user_id === (await currentUserId());
    } catch {
      return true;
    }
  },

  // Update the signed-in user's own creator row. Scoped to `user_id` so the
  // filter matches the RLS update policy (`user_id = auth.uid()`) — the policy
  // is the boundary, this just keeps PostgREST from touching anyone else's row.
  // Onboarding persists one step at a time, so a partially-built profile is
  // finished by updating the row it already owns rather than inserting another.
  // No-ops in demo mode; throws on a real write error so the caller can surface
  // a failure toast.
  async updateMyCreator(fields: NewCreatorFields): Promise<CreatorRow | null> {
    if (!supabase) return null;
    const userId = await currentUserId();
    if (!userId) return null;

    const { data, error } = await supabase
      .from('creators')
      .update(fields)
      .eq('user_id', userId)
      .select()
      .single();
    if (error) throw handleConstraintError(error) ?? error;
    return (data as CreatorRow | null) ?? null;
  },

  // Read a creator's saved "My Pitch" text. Returns null in demo mode (no
  // client), when the row/column is empty, or if the read fails — callers fall
  // back to the bundled default so the editor still renders.
  async getCreatorPitch(creatorId: number): Promise<string | null> {
    if (!supabase) return null;
    const { data } = await supabase.from('creators').select('pitch').eq('id', creatorId).single();
    return data?.pitch ?? null;
  },

  // Persist a creator's "My Pitch" text to creators.pitch. No-ops in demo mode
  // so the editor keeps working without a backend; throws on a real write error
  // so the caller can surface a failure toast. Callers own length validation.
  async updateCreatorPitch(creatorId: number, pitch: string): Promise<void> {
    if (!supabase) return;
    const { error } = await supabase.from('creators').update({ pitch }).eq('id', creatorId);
    if (error) throw error;
  },

  async addReview(creatorId: number, review: CreatorReview, authorId?: string): Promise<void> {
    if (!supabase) return;
    await supabase.from('creator_reviews').insert({
      creator_id: creatorId,
      author_id: authorId ?? null,
      brand: review.brand,
      rating: review.rating,
      review_text: review.text,
      review_date: review.date,
    });
  },

  // ── SAVED CREATORS (per-user vault) ────────────────────────────────────────

  async getSavedCreatorIds(): Promise<number[]> {
    if (!supabase) return [];
    const { data } = await supabase.from('saved_creators').select('creator_id');
    return (data ?? []).map((r) => r.creator_id);
  },

  async saveCreator(userId: string | undefined, creatorId: number): Promise<void> {
    if (!supabase) return;
    await supabase.from('saved_creators').insert({ user_id: userId, creator_id: creatorId });
  },

  async unsaveCreator(userId: string | undefined, creatorId: number): Promise<void> {
    if (!supabase) return;
    await supabase
      .from('saved_creators')
      .delete()
      .eq('user_id', userId)
      .eq('creator_id', creatorId);
  },

  // ── CREATOR MEDIA (Supabase Storage) ───────────────────────────────────────

  // Upload an avatar or portfolio image to `<uid>/…` and return its storage
  // key. The path prefix is the caller's own uid, which is exactly what the
  // bucket's write policy checks — RLS is the boundary, this just builds a key
  // that satisfies it. Returns null in demo mode or without a session; throws
  // on a real upload failure so the caller can surface a toast.
  async uploadCreatorMedia(file: File, kind: 'avatar' | 'portfolio'): Promise<string | null> {
    if (!supabase) return null;
    const userId = await currentUserId();
    if (!userId) return null;

    const path = mediaPath(userId, kind, imageExtension(file), mediaSeed());
    const { error } = await supabase.storage.from(CREATOR_MEDIA_BUCKET).upload(path, file, {
      contentType: file.type,
      upsert: true,
    });
    if (error) throw error;
    return path;
  },

  // Mint short-lived signed URLs for stored media, keyed by path.
  //
  // Batched deliberately: Discover renders a wall of avatars, and signing them
  // one at a time would be a round trip per card. Missing or unsignable paths
  // are simply absent from the result, so a caller renders its emoji fallback
  // rather than a broken image.
  async getMediaUrls(
    paths: readonly (string | null | undefined)[]
  ): Promise<Record<string, string>> {
    const wanted = [...new Set(paths.filter((p): p is string => Boolean(p)))];
    if (!supabase || wanted.length === 0) return {};
    const { data, error } = await supabase.storage
      .from(CREATOR_MEDIA_BUCKET)
      .createSignedUrls(wanted, MEDIA_URL_TTL);
    if (error || !data) return {};
    // `path` is nullable on the rows Storage returns for keys it could not
    // sign; those simply don't make it into the map, and their callers fall
    // back to the emoji placeholder.
    return data.reduce<Record<string, string>>((urls, row) => {
      if (row.path && row.signedUrl) urls[row.path] = row.signedUrl;
      return urls;
    }, {});
  },

  // Delete creator media (replacing an avatar, removing a portfolio image).
  // Best-effort: never throws, so a failed cleanup can't block a profile save.
  async removeCreatorMedia(paths: readonly (string | null | undefined)[]): Promise<void> {
    const wanted = paths.filter((p): p is string => Boolean(p));
    if (!supabase || wanted.length === 0) return;
    try {
      await supabase.storage.from(CREATOR_MEDIA_BUCKET).remove([...wanted]);
    } catch {
      // Best-effort cleanup: a failed delete must not unwind a save.
    }
  },

  // ── CAMPAIGNS ──────────────────────────────────────────────────────────────

  // The brand's own campaigns, at every status.
  //
  // Scoped to owner_id explicitly since PIT-75: the campaigns select policy now
  // also admits any Active campaign, so an unscoped `select *` would pull other
  // brands' briefs into this brand's "my campaigns" list. RLS stopped shaping
  // this query the moment it started allowing more through — the same trap
  // PIT-63 hit with Discover.
  async getCampaigns(): Promise<CampaignRecord[]> {
    if (!supabase) return DEMO_CAMPAIGNS;
    const userId = await currentUserId();
    if (!userId) return [];
    const { data } = await supabase
      .from('campaigns')
      .select('*')
      .eq('owner_id', userId)
      .order('id');
    return data ?? [];
  },

  // The creator-facing feed: campaigns open to applications, from any brand
  // (PIT-75).
  //
  // Filtered at the query level rather than in the view so a paused or
  // in-progress campaign never reaches the client. This narrows what the select
  // policy allows instead of substituting for it — the policy is what stops a
  // creator reading someone's paused draft; this clause is what keeps the feed
  // to campaigns that are actually taking applications.
  async getOpenCampaigns(): Promise<CampaignRecord[]> {
    if (!supabase) return DEMO_CAMPAIGNS.filter((c) => c.status === OPEN_CAMPAIGN_STATUS);
    const { data } = await supabase
      .from('campaigns')
      .select('*')
      .eq('status', OPEN_CAMPAIGN_STATUS)
      .order('id', { ascending: false });
    return data ?? [];
  },

  async createCampaign(
    ownerId: string | undefined,
    campaign: Omit<Campaign, 'id' | 'brief'>
  ): Promise<Campaign | null> {
    if (!supabase || !ownerId) return null;
    const { data } = await supabase
      .from('campaigns')
      .insert({ ...campaign, owner_id: ownerId })
      .select()
      .single();
    return data;
  },

  // Move a campaign between statuses — the same write in both directions.
  //
  // Replaces the one-way pauseCampaign. Since PIT-75 the status governs
  // marketplace visibility, so a pause with no way back removed a campaign from
  // the creator feed permanently (PIT-85). One function keeps that a single
  // policy surface, as setApplicationStatus does for applications.
  //
  // Who may set it is the "own campaigns" policy's business — owner only, on
  // both `using` and `with check` — and which values exist is the
  // campaigns_status_known constraint's. A refused write comes back as no row
  // rather than an error, so the caller can say so plainly.
  async setCampaignStatus(
    id: number | string | undefined,
    status: CampaignStatus
  ): Promise<CampaignRow | null> {
    // Unsaved local rows (string `local-*` ids) only ever exist in demo mode,
    // where there is no client to write to in the first place.
    if (!supabase || id === undefined || String(id).startsWith('local-')) return null;
    const { data, error } = await supabase
      .from('campaigns')
      .update({ status })
      .eq('id', id)
      .select()
      .maybeSingle();
    if (error) throw error;
    return (data as CampaignRow | null) ?? null;
  },

  async updateCampaign(
    id: number | string | undefined,
    fields: Partial<CampaignRow>
  ): Promise<void> {
    // Skip unsaved local rows (string `local-*` ids) and absent ids — neither
    // exists in the DB, so there is nothing to update.
    if (!supabase || id === undefined || String(id).startsWith('local-')) return;
    await supabase.from('campaigns').update(fields).eq('id', id);
  },

  // ── APPLICATIONS (PIT-77) ──────────────────────────────────────────────────

  // Submit a creator's pitch to a campaign.
  //
  // `creator_id` and `user_id` are resolved here rather than taken from the
  // caller: the database trigger rejects a mismatched pair anyway, and asking a
  // view to supply its own identity is how that mismatch happens. Throws
  // ApplicationExistsError when the creator has already applied — the unique
  // index is what decides, and two tabs will race it.
  async applyToCampaign(fields: NewApplicationFields): Promise<ApplicationRow | null> {
    if (!supabase) return null;
    const userId = await currentUserId();
    if (!userId) return null;
    const creator = await ownCreatorRow(userId);
    // No profile means nothing to apply as. The apply flow routes to onboarding
    // before reaching here; this is the backstop.
    if (!creator) return null;

    const { data, error } = await supabase
      .from('applications')
      .insert({
        campaign_id: fields.campaign_id,
        creator_id: creator.id,
        user_id: userId,
        pitch_text: fields.pitch_text,
        status: INITIAL_APPLICATION_STATUS,
      })
      .select()
      .single();
    if (error) {
      if (isDuplicateApplication(error)) {
        throw new ApplicationExistsError('You have already applied to this campaign.');
      }
      throw error;
    }
    return (data as ApplicationRow | null) ?? null;
  },

  // The signed-in creator's own applications, newest first. Scoped to user_id
  // explicitly: the select policy also admits applications on campaigns this
  // user owns as a brand, and those are someone else's pitches — not this
  // creator's outbox.
  async getMyApplications(): Promise<Application[]> {
    if (!supabase) return [];
    const userId = await currentUserId();
    if (!userId) return [];
    // The campaign comes along so the dashboard can name what was applied to.
    // PIT-82's policy is what keeps it readable after the brand pauses it.
    const { data } = await supabase
      .from('applications')
      .select('*, campaigns(*)')
      .eq('user_id', userId)
      .order('id', { ascending: false });
    return ((data as (ApplicationRow & { campaigns?: CampaignRow | null })[] | null) ?? []).map(
      (row) => ({ ...row, campaign: row.campaigns ?? null })
    );
  },

  // Applications on a campaign, with the creator behind each, for the brand's
  // review list. RLS returns nothing unless the caller owns the campaign.
  async getCampaignApplications(campaignId: number): Promise<Application[]> {
    if (!supabase) return [];
    const { data } = await supabase
      .from('applications')
      .select('*, creators(*)')
      .eq('campaign_id', campaignId)
      .order('id', { ascending: false });
    return ((data as (ApplicationRow & { creators?: Creator | null })[] | null) ?? []).map(
      (row) => ({ ...row, creator: row.creators ?? null })
    );
  },

  // Applications across several campaigns at once, with the creator behind
  // each. One query for the brand's whole dashboard rather than one per card —
  // the same batching reason getMediaUrls and getBriefUrls exist.
  //
  // RLS still decides: campaigns the caller does not own contribute nothing,
  // so passing a wider list than you own is safe rather than a leak.
  async getApplicationsForCampaigns(campaignIds: readonly number[]): Promise<Application[]> {
    if (!supabase || campaignIds.length === 0) return [];
    const { data } = await supabase
      .from('applications')
      .select('*, creators(*)')
      .in('campaign_id', [...campaignIds])
      .order('id', { ascending: false });
    return ((data as (ApplicationRow & { creators?: Creator | null })[] | null) ?? []).map(
      (row) => ({ ...row, creator: row.creators ?? null })
    );
  },

  // Move an application to a terminal status. Who may set which value is the
  // policies' business, not this function's: a brand may set accepted/declined,
  // the applicant may set withdrawn, and neither can touch a row that has
  // already left `submitted`. A rejected attempt surfaces as zero rows updated
  // rather than an error, so the caller is told plainly.
  async setApplicationStatus(
    id: number,
    status: ApplicationStatus
  ): Promise<ApplicationRow | null> {
    if (!supabase) return null;
    const { data, error } = await supabase
      .from('applications')
      .update({ status })
      .eq('id', id)
      .select()
      .maybeSingle();
    if (error) throw error;
    return (data as ApplicationRow | null) ?? null;
  },

  // ── CAMPAIGN BRIEFS (Supabase Storage) ─────────────────────────────────────

  // Upload a validated brief PDF to `<ownerId>/<campaignId>/<filename>` and
  // return the stored { path, name }. Overwrites any object already at that
  // path (upsert) so re-saving the same filename is safe. Throws on failure so
  // the caller can surface a toast; returns null in demo mode (no client).
  async uploadBrief(
    ownerId: string | undefined,
    campaignId: number | string | undefined,
    file: File | null
  ): Promise<{ path: string; name: string } | null> {
    if (!supabase || !ownerId || !campaignId || !file) return null;
    const path = `${ownerId}/${campaignId}/${file.name}`;
    const { error } = await supabase.storage.from(BRIEF_BUCKET).upload(path, file, {
      contentType: 'application/pdf',
      upsert: true,
    });
    if (error) throw error;
    return { path, name: file.name };
  },

  // Mint a short-lived signed URL for a stored brief so a private-bucket object
  // can be downloaded from the campaign card. Returns null in demo mode, when
  // no path is stored, or if signing fails (card simply omits the link).
  async getBriefUrl(path: string | null | undefined): Promise<string | null> {
    if (!supabase || !path) return null;
    const { data, error } = await supabase.storage
      .from(BRIEF_BUCKET)
      .createSignedUrl(path, BRIEF_URL_TTL);
    if (error) return null;
    return data?.signedUrl ?? null;
  },

  // Mint signed brief URLs for a whole screen in one call, keyed by path.
  //
  // The campaign feed shows many campaigns at once, and signing one at a time
  // would be a round trip per card — the same reason getMediaUrls batches
  // (PIT-71). Paths that cannot be signed are simply absent from the result, so
  // a card renders without a download link rather than breaking.
  async getBriefUrls(
    paths: readonly (string | null | undefined)[]
  ): Promise<Record<string, string>> {
    const wanted = [...new Set(paths.filter((p): p is string => Boolean(p)))];
    if (!supabase || wanted.length === 0) return {};
    const { data, error } = await supabase.storage
      .from(BRIEF_BUCKET)
      .createSignedUrls(wanted, BRIEF_URL_TTL);
    if (error || !data) return {};
    return data.reduce<Record<string, string>>((urls, row) => {
      if (row.path && row.signedUrl) urls[row.path] = row.signedUrl;
      return urls;
    }, {});
  },

  // Delete a stored brief (used when replacing or removing a campaign's brief).
  // Best-effort: never throws, so a failed cleanup can't block the save.
  async removeBrief(path: string | null | undefined): Promise<void> {
    if (!supabase || !path) return;
    try {
      await supabase.storage.from(BRIEF_BUCKET).remove([path]);
    } catch {
      // Best-effort cleanup: a failed delete must not block or unwind a save.
    }
  },

  // ── PRODUCTS ───────────────────────────────────────────────────────────────

  async getProducts(): Promise<Product[]> {
    if (!supabase) return DEMO_PRODUCTS;
    const { data } = await supabase.from('products').select('*').order('id');
    return data ?? [];
  },

  async addProduct(
    ownerId: string | undefined,
    product: Omit<Product, 'id'>
  ): Promise<Product | null> {
    if (!supabase || !ownerId) return null;
    const { data } = await supabase
      .from('products')
      .insert({ ...product, owner_id: ownerId })
      .select()
      .single();
    return data;
  },

  // ── CHATS & MESSAGES ───────────────────────────────────────────────────────

  // Conversations the caller is part of, from either side (PIT-80).
  //
  // Deliberately unscoped in the query, unlike getCampaigns: "the chats I am in"
  // is exactly what the participants policy returns, so narrowing here would
  // mean re-stating the rule in a second place and risking the two disagreeing.
  // Where a query filter earns its place is when it must be *narrower* than the
  // policy — the Discover and campaign-feed cases.
  async getChats(): Promise<Chat[]> {
    if (!supabase) return DEMO_CHATS.map((c) => ({ ...c, messages: [...c.messages] }));
    const { data } = await supabase.from('chats').select('*, messages(*)').order('id');
    // The viewer decides which side's label the thread shows — see mapChat.
    // Not a filter: the policy has already decided which rows come back.
    const viewerId = await currentUserId();

    // Read markers come back in the same pass rather than per thread. The
    // select policy already returns only this user's rows, so there is nothing
    // to scope here (PIT-86).
    const { data: reads } = await supabase
      .from('chat_reads')
      .select('chat_id, last_read_message_id');
    const lastRead = new Map<number, number>(
      ((reads as { chat_id: number; last_read_message_id: number }[] | null) ?? []).map((r) => [
        r.chat_id,
        r.last_read_message_id,
      ])
    );

    return (data ?? []).map((row) => mapChat(row, viewerId, lastRead.get(row.id) ?? 0));
  },

  // Record that the caller has read this conversation up to `messageId`.
  //
  // Only ever the caller's own marker: the row is keyed by user, the policy is
  // `user_id = auth.uid()`, and a database trigger keeps the marker moving
  // forward so a stale tab cannot undo a newer read (PIT-86).
  async markChatRead(chatId: number | null, messageId: number): Promise<void> {
    if (!supabase || chatId === null) return;
    const userId = await currentUserId();
    if (!userId) return;
    await supabase
      .from('chat_reads')
      .upsert(
        { chat_id: chatId, user_id: userId, last_read_message_id: messageId },
        { onConflict: 'chat_id,user_id' }
      );
  },

  // Open the conversation behind an accepted application, returning its chat id
  // (or null when the caller may not, or the application isn't accepted).
  //
  // One RPC rather than a chat insert followed by a message insert: the two
  // have to land together, and a half-written thread could not be repaired by
  // retrying, because the unique index would reject the second attempt. The
  // function also stamps the seeded pitch with the creator who wrote it, which
  // the brand's own session could not do — see the PIT-81 migration.
  //
  // Idempotent, so callers need not check first: asking twice returns the same
  // conversation rather than opening a second one.
  async openConversationForApplication(applicationId: number): Promise<number | null> {
    if (!supabase) return null;
    const { data, error } = await supabase.rpc('open_conversation_for_application', {
      p_application_id: applicationId,
    });
    if (error) throw error;
    return (data as number | null) ?? null;
  },

  async addMessage(
    chatId: number | null,
    msg: ChatMessage,
    newRevisionCount?: number
  ): Promise<void> {
    if (!supabase) return;
    if (chatId === null) return; // no active chat to attach the message to
    // The sender is taken from the session, never from the caller. PIT-80's
    // insert policy requires sender_user_id = auth.uid(), so a message cannot
    // be attributed to someone who did not write it — `from_role` remains only
    // a rendering hint, which is all it ever reliably was.
    const senderId = await currentUserId();
    if (!senderId) return;
    const ops = [
      supabase.from('messages').insert({
        chat_id: chatId,
        sender_user_id: senderId,
        from_role: msg.from,
        message_text: msg.text ?? null,
        file_name: msg.file ?? null,
        file_type: msg.fileType ?? null,
        delivery: msg.delivery ?? false,
        sent_time: msg.time,
      }),
      supabase
        .from('chats')
        .update({ preview: msg.text || `Uploaded ${msg.file}` })
        .eq('id', chatId),
    ];
    if (newRevisionCount !== undefined) {
      ops.push(supabase.from('chats').update({ revisions: newRevisionCount }).eq('id', chatId));
    }
    await Promise.all(ops);
  },

  async markFinalDelivery(messageId: number | undefined, chatId: number | null): Promise<void> {
    if (!supabase) return;
    if (messageId === undefined || chatId === null) return; // nothing persisted to mark
    await Promise.all([
      supabase.from('messages').update({ delivery: true }).eq('id', messageId),
      supabase
        .from('chats')
        .update({ status: 'Delivered', status_class: 'status-delivered' })
        .eq('id', chatId),
    ]);
  },
};
